import { useState, useEffect } from 'react';
import { RefreshCw, Activity, Zap, Network, BarChart3, Server, Clock, PieChart, Layers, AlertCircle } from 'lucide-react';
import StatCard from '../components/common/StatCard';
import EdgeSelector from '../components/common/EdgeSelector';
import { observabilityService } from '../services';
import applicationService from '../services/application.service';
import type { ApplicationDefinition } from '../services/application.service';
import vppMonitoringService from '../services/vppMonitoring.service';
import { toast } from 'react-hot-toast';
import { useSettingsStore } from '../stores/settingsStore';
import { useTenantStore } from '../stores/tenantStore';
import { formatTimeOnly } from '../utils/formatDate';

// Protocol number to name mapping
const PROTOCOL_NAMES: Record<string, string> = {
  '1': 'ICMP',
  '6': 'TCP',
  '17': 'UDP',
  '47': 'GRE',
  '50': 'ESP',
  '58': 'ICMPv6',
  'icmp': 'ICMP',
  'tcp': 'TCP',
  'udp': 'UDP',
  'gre': 'GRE',
  'esp': 'ESP',
  'icmpv6': 'ICMPv6',
};

// Common port to service name mapping
const PORT_NAMES: Record<string, string> = {
  '22': 'SSH',
  '53': 'DNS',
  '80': 'HTTP',
  '443': 'HTTPS',
  '853': 'DNS-over-TLS',
  '1883': 'MQTT',
  '3306': 'MySQL',
  '5432': 'PostgreSQL',
  '6379': 'Redis',
  '8080': 'HTTP-Alt',
  '8443': 'HTTPS-Alt',
  'high': 'High Ports (1024+)',
};

interface TrafficByProtocol {
  protocol: string;
  protocol_name: string;
  total_bytes: number;
  percentage: number;
}

interface TrafficByPort {
  port: string;
  service_name: string;
  total_bytes: number;
  percentage: number;
}

interface FlowStats {
  total_bytes: number;
  total_packets: number;
  total_flows: number;
  bandwidth_mbps: number;
}

interface BandwidthPoint {
  timestamp: Date;
  bytes: number;
  bandwidth_mbps: number;
}

const TrafficMonitoring = () => {
  const { getDateFormatSettings } = useSettingsStore();
  const dateSettings = getDateFormatSettings();
  const { getTenantId } = useTenantStore();
  // Subscribe to selectedTenantId as a value so the app-definitions
  // useEffect re-fires on tenant switch — getTenantId is a stable Zustand
  // function reference, so a [getTenantId]-only dep array never triggers
  // a refetch when the user changes tenant. (VSNP-670 audit)
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string>('');
  const [portToAppMap, setPortToAppMap] = useState<Record<string, string>>({});
  const [appDefinitions, setAppDefinitions] = useState<ApplicationDefinition[]>([]);
  const [flowStats, setFlowStats] = useState<FlowStats | null>(null);
  const [protocolData, setProtocolData] = useState<TrafficByProtocol[]>([]);
  const [portData, setPortData] = useState<TrafficByPort[]>([]);
  const [bandwidthTimeline, setBandwidthTimeline] = useState<BandwidthPoint[]>([]);
  const [hasData, setHasData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [hoursBack, setHoursBack] = useState(1);
  const [ipfixEnabled, setIpfixEnabled] = useState<boolean | null>(null);

  // Fetch application definitions to build port→app name lookup
  useEffect(() => {
    const fetchApps = async () => {
      try {
        const tenantId = getTenantId();
        // Fetch all apps — API caps at 100 per page, loop if needed
        let allApps: ApplicationDefinition[] = [];
        let page = 1;
        let hasMore = true;
        while (hasMore) {
          const res = await applicationService.list({ pageSize: 100, page, ...(tenantId ? { tenantId } : {}) });
          allApps = allApps.concat(res.items);
          hasMore = page < res.totalPages;
          page++;
        }
        const res = { items: allApps };
        const apps = res.items.filter(a => a.enabled === 1);
        setAppDefinitions(apps);

        // Build port→app name map from destination_ports
        // Handles: single ports ("443"), comma-separated ("500,4500"), ranges ("67-68")
        const map: Record<string, string> = {};
        for (const app of apps) {
          if (!app.destination_ports || app.destination_ports === 'any') continue;
          for (const segment of app.destination_ports.split(',')) {
            const s = segment.trim();
            if (!s) continue;
            const rangeParts = s.split('-');
            if (rangeParts.length === 2) {
              const lo = parseInt(rangeParts[0], 10);
              const hi = parseInt(rangeParts[1], 10);
              if (!isNaN(lo) && !isNaN(hi) && hi - lo < 100) {
                for (let p = lo; p <= hi; p++) {
                  if (!map[String(p)]) map[String(p)] = app.name;
                }
              }
            } else if (!map[s]) {
              map[s] = app.name;
            }
          }
        }
        setPortToAppMap(map);
      } catch {
        // Apps not available — will fall back to PORT_NAMES
      }
    };
    fetchApps();
  }, [getTenantId, selectedTenantId]);

  // Fetch statistics when edge is selected or hoursBack changes
  useEffect(() => {
    if (selectedEdgeId) {
      fetchStatistics();
      // Auto-refresh every 60 seconds
      const interval = setInterval(fetchStatistics, 60000);
      return () => clearInterval(interval);
    }
  }, [selectedEdgeId, hoursBack, portToAppMap]);


  const fetchStatistics = async () => {
    if (!selectedEdgeId) return;

    setIsLoading(true);
    try {
      const now = Math.floor(Date.now() / 1000);
      const start = now - hoursBack * 3600;
      const step = Math.max(60, Math.floor((now - start) / 100)); // ~100 data points

      // Check if IPFIX is enabled (DB configuration + Metric confirmation)
      let isConfigEnabled = false;
      try {
        const flowprobeRes = await vppMonitoringService.getFlowprobeConfig(selectedEdgeId);
        isConfigEnabled = !!flowprobeRes?.config?.enabled;
      } catch (err) {
        console.warn('Failed to fetch flowprobe config from DB, relying on metrics only');
      }

      const enabledQuery = `edge_ipfix_enabled{edge_id="${selectedEdgeId}"}`;
      const enabledResp = await observabilityService.query(enabledQuery);
      const enabledValue = enabledResp.data?.result?.[0]?.value?.[1];
      const isMetricEnabled = enabledValue === '1';

      // IPFIX is considered enabled if either the DB says so (intended) or the metric confirms it (actual)
      setIpfixEnabled(isConfigEnabled || isMetricEnabled);

      // Query total bytes by protocol
      const bytesQuery = `sum by (protocol) (increase(edge_ipfix_bytes_total{edge_id="${selectedEdgeId}"}[${hoursBack}h]))`;
      const bytesResp = await observabilityService.query(bytesQuery);

      // Query total packets by protocol
      const packetsQuery = `sum by (protocol) (increase(edge_ipfix_packets_total{edge_id="${selectedEdgeId}"}[${hoursBack}h]))`;
      const packetsResp = await observabilityService.query(packetsQuery);

      // Query total flows by protocol
      const flowsQuery = `sum by (protocol) (increase(edge_ipfix_flows_total{edge_id="${selectedEdgeId}"}[${hoursBack}h]))`;
      const flowsResp = await observabilityService.query(flowsQuery);

      // Query bytes by port
      const byPortQuery = `sum by (dst_port) (increase(edge_ipfix_bytes_total{edge_id="${selectedEdgeId}"}[${hoursBack}h]))`;
      const byPortResp = await observabilityService.query(byPortQuery);

      // Query bandwidth timeline
      const timelineQuery = `sum(rate(edge_ipfix_bytes_total{edge_id="${selectedEdgeId}"}[5m])) * 8 / 1000000`;
      const timelineResp = await observabilityService.queryRange(timelineQuery, start, now, step);

      // Process results
      let totalBytes = 0;
      let totalPackets = 0;
      let totalFlows = 0;

      // Process bytes by protocol
      const protocolBytes = new Map<string, number>();
      if (bytesResp.data?.result) {
        for (const result of bytesResp.data.result) {
          const protocol = result.metric.protocol || 'unknown';
          const bytes = parseFloat(result.value?.[1] || '0');
          protocolBytes.set(protocol, bytes);
          totalBytes += bytes;
        }
      }

      // Process packets
      if (packetsResp.data?.result) {
        for (const result of packetsResp.data.result) {
          const packets = parseFloat(result.value?.[1] || '0');
          totalPackets += packets;
        }
      }

      // Process flows
      if (flowsResp.data?.result) {
        for (const result of flowsResp.data.result) {
          const flows = parseFloat(result.value?.[1] || '0');
          totalFlows += flows;
        }
      }

      // Build protocol breakdown
      const protocols: TrafficByProtocol[] = Array.from(protocolBytes.entries())
        .map(([protocol, bytes]) => ({
          protocol,
          protocol_name: PROTOCOL_NAMES[protocol] || `Protocol ${protocol}`,
          total_bytes: bytes,
          percentage: totalBytes > 0 ? (bytes / totalBytes) * 100 : 0,
        }))
        .sort((a, b) => b.total_bytes - a.total_bytes);

      // Process bytes by port
      const portBytes = new Map<string, number>();
      if (byPortResp.data?.result) {
        for (const result of byPortResp.data.result) {
          const port = result.metric.dst_port || 'unknown';
          const bytes = parseFloat(result.value?.[1] || '0');
          portBytes.set(port, bytes);
        }
      }

      // Build port breakdown
      const totalPortBytes = Array.from(portBytes.values()).reduce((a, b) => a + b, 0);
      const ports: TrafficByPort[] = Array.from(portBytes.entries())
        .map(([port, bytes]) => ({
          port,
          service_name: portToAppMap[port] || PORT_NAMES[port] || `Port ${port}`,
          total_bytes: bytes,
          percentage: totalPortBytes > 0 ? (bytes / totalPortBytes) * 100 : 0,
        }))
        .sort((a, b) => b.total_bytes - a.total_bytes)
        .slice(0, 10);

      // Process bandwidth timeline
      const timeline: BandwidthPoint[] = [];
      if (timelineResp.data?.result?.[0]?.values) {
        for (const [timestamp, value] of timelineResp.data.result[0].values) {
          const bw = parseFloat(value);
          timeline.push({
            timestamp: new Date(timestamp * 1000),
            bytes: 0,
            bandwidth_mbps: bw,
          });
        }
      }

      // Calculate average bandwidth
      const avgBandwidth = timeline.length > 0
        ? timeline.reduce((sum, p) => sum + p.bandwidth_mbps, 0) / timeline.length
        : 0;

      // Set state
      const hasAnyData = totalBytes > 0 || totalPackets > 0 || totalFlows > 0;
      setHasData(hasAnyData);

      setFlowStats({
        total_bytes: totalBytes,
        total_packets: totalPackets,
        total_flows: totalFlows,
        bandwidth_mbps: Math.round(avgBandwidth * 100) / 100,
      });

      setProtocolData(protocols);
      setPortData(ports);
      setBandwidthTimeline(timeline);
      setLastUpdate(new Date());

    } catch (error) {
      console.error('Failed to fetch statistics:', error);
      toast.error('Failed to load statistics');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchStatistics();
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(2) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(2) + 'K';
    }
    return Math.round(num).toString();
  };

  const stats = flowStats;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Traffic Monitoring</h1>
          <p className="text-sm text-gray-500 mt-1">Real network traffic from IPFIX flow metrics</p>
        </div>
        <div className="flex items-center space-x-3">
          {lastUpdate && (
            <span className="text-sm text-gray-500 hidden sm:flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              {formatTimeOnly(lastUpdate, dateSettings)}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="btn-secondary inline-flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Edge and Time Range Selection */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Edge Device</label>
          <EdgeSelector
            selectedEdgeId={selectedEdgeId}
            onEdgeChange={setSelectedEdgeId}
          />
        </div>
        <div className="w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
          <select
            value={hoursBack}
            onChange={(e) => setHoursBack(Number(e.target.value))}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value={1}>Last 1 hour</option>
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={48}>Last 48 hours</option>
            <option value={168}>Last 7 days</option>
          </select>
        </div>
      </div>

      {/* Data Status Banner */}
      {ipfixEnabled === false && (
        <div className="bg-amber-50 border-l-4 border-amber-400 p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-amber-500 mr-2" />
            <p className="text-sm text-amber-700">
              <strong>IPFIX collection is disabled.</strong> Enable flowprobe on this edge
              to collect network traffic statistics. Go to Edge Settings &gt; Flow Export.
            </p>
          </div>
        </div>
      )}

      {ipfixEnabled === true && hasData && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <p className="text-sm text-green-700">
            <strong>Live flow data active.</strong> Showing IPFIX traffic metrics from VictoriaMetrics.
          </p>
        </div>
      )}

      {stats && hasData && (
        <>
          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <StatCard
              icon={Activity}
              title="Total Flows"
              value={formatNumber(stats.total_flows)}
              subtitle={`Last ${hoursBack}h`}
              color="primary"
            />
            <StatCard
              icon={Network}
              title="Total Traffic"
              value={formatBytes(stats.total_bytes)}
              subtitle={`${formatNumber(stats.total_packets)} packets`}
              color="info"
            />
            <StatCard
              icon={Zap}
              title="Bandwidth"
              value={`${stats.bandwidth_mbps} Mbps`}
              subtitle="Average"
              color="success"
            />
            <StatCard
              icon={Clock}
              title="Period"
              value={`${hoursBack}h`}
              subtitle="Time range"
              color="secondary"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Traffic by Protocol */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Server className="w-5 h-5 mr-2 text-primary-600" />
                  Traffic by Protocol
                </h3>

                {protocolData.length > 0 ? (
                  <div className="space-y-3">
                    {protocolData.map((proto) => (
                      <div key={proto.protocol} className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-900">
                            {proto.protocol_name}
                          </span>
                          <span className="text-gray-600">
                            {formatBytes(proto.total_bytes)} ({proto.percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${proto.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No protocol data available</p>
                )}
              </div>
            </div>

            {/* Top Ports */}
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-info-600" />
                  Top Ports
                </h3>

                {portData.length > 0 ? (
                  <div className="space-y-3">
                    {portData.map((port) => (
                      <div key={port.port} className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="font-medium text-gray-900">
                            {port.service_name}
                            {port.port !== 'high' && port.port !== 'unknown' && (
                              <span className="text-gray-400 ml-1">:{port.port}</span>
                            )}
                          </span>
                          <span className="text-gray-600">
                            {formatBytes(port.total_bytes)} ({port.percentage.toFixed(1)}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-info-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${port.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No port data available</p>
                )}
              </div>
            </div>
          </div>

          {/* Application Mapping — resolved from DB application definitions */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Layers className="w-5 h-5 mr-2 text-emerald-600" />
                Application Mapping
              </h3>
              {(() => {
                // Match traffic ports to application definitions
                const matchedApps = portData
                  .filter(p => portToAppMap[p.port])
                  .map(p => {
                    const appName = portToAppMap[p.port];
                    return {
                      name: appName,
                      port: p.port,
                      bytes: p.total_bytes,
                      percentage: p.percentage,
                      app: appDefinitions.find(a => a.name === appName),
                    };
                  });

                if (matchedApps.length > 0) {
                  return (
                    <div className="space-y-2">
                      {matchedApps.map(app => (
                        <div key={app.port} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-900">{app.name}</span>
                            <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 font-mono">:{app.port}</span>
                            {app.app?.category && (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">{app.app.category}</span>
                            )}
                          </div>
                          <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-gray-700">
                              {app.bytes >= 1073741824 ? `${(app.bytes / 1073741824).toFixed(1)} GB`
                                : app.bytes >= 1048576 ? `${(app.bytes / 1048576).toFixed(1)} MB`
                                : `${(app.bytes / 1024).toFixed(0)} KB`}
                            </span>
                            <span className="text-xs text-gray-500">{app.percentage.toFixed(1)}%</span>
                          </div>
                        </div>
                      ))}
                      <p className="text-xs text-gray-400 mt-2">
                        {appDefinitions.length} applications configured — port-to-application mapping from <a href="/applications" className="text-primary-600 hover:underline">Applications</a>
                      </p>
                    </div>
                  );
                }

                return (
                  <div className="text-center py-4">
                    <PieChart className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">
                      {appDefinitions.length > 0
                        ? 'No application definitions match the current traffic ports.'
                        : 'No application definitions configured.'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {appDefinitions.length > 0
                        ? <>Manage port mappings in <a href="/applications" className="text-primary-600 hover:underline">Applications</a>.</>
                        : <>Define applications in <a href="/applications" className="text-primary-600 hover:underline">Applications</a> to map ports to application names.</>}
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Bandwidth Timeline */}
          {bandwidthTimeline.length > 0 && (
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Activity className="w-5 h-5 mr-2 text-warning-600" />
                  Bandwidth Timeline
                </h3>

                <div className="space-y-2">
                  {/* Simple bar chart representation */}
                  <div className="flex items-end h-32 space-x-1">
                    {bandwidthTimeline.slice(-48).map((point, index) => {
                      const maxBw = Math.max(...bandwidthTimeline.map(p => p.bandwidth_mbps), 0.1);
                      const height = (point.bandwidth_mbps / maxBw) * 100;
                      return (
                        <div
                          key={index}
                          className="flex-1 bg-primary-500 rounded-t transition-all duration-200 hover:bg-primary-600"
                          style={{ height: `${Math.max(height, 2)}%` }}
                          title={`${formatTimeOnly(point.timestamp, dateSettings)}: ${point.bandwidth_mbps.toFixed(2)} Mbps`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>{bandwidthTimeline[0]?.timestamp ? formatTimeOnly(bandwidthTimeline[0].timestamp, dateSettings) : ''}</span>
                    <span>Now</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {!hasData && !isLoading && selectedEdgeId && (
        <div className="card">
          <div className="p-12 text-center">
            <Activity className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Flow Data Available</h3>
            <p className="text-gray-600 mb-4">
              No IPFIX flow data has been collected for this edge in the selected time range.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {ipfixEnabled === false
                ? 'Enable IPFIX flowprobe export on the edge to start collecting traffic statistics.'
                : 'Data may take a few minutes to appear after enabling flowprobe.'}
            </p>
            <button onClick={handleRefresh} className="btn-primary">
              Check Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrafficMonitoring;
